import {Tokens} from '../../entities/authentication/tokens.entity';
import {Injectable} from '@angular/core';
import {User} from '../../entities/user/user.entity';
import {jwtDecode} from 'jwt-decode';
import {catchError, map, Observable, of, tap, throwError} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import {LocalStorageService} from '../localstorage/local-storage.service';


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService
{
  private tokenStorageKey = 'tokens';

  public constructor(
    private readonly http: HttpClient,
    private readonly localStorage: LocalStorageService,
  ) {
  }

  public login(email: string, password: string): Observable<boolean> {
    return this.http.post<Tokens>(`${environment.apiUrl}/api/v1/auth/login`, {
      email: email,
      password: password
    }, {
      withCredentials: true
    }).pipe(
      tap(tokens => this.saveTokens(tokens)), // Save tokens on success
      map(() => true), // Return true if login succeeds
      catchError(() => of(false)) // Return false if login fails
    );
  }


  public refreshToken(): Observable<Tokens> {
    const refreshToken = this.getTokens()?.refresh_token;
    const payload = refreshToken ? { refresh_token: refreshToken } : {};
    return this.http.post<Tokens>(`${environment.apiUrl}/api/v1/auth/refresh`, payload, { withCredentials: true })
      .pipe(
        tap(tokens => {
          this.saveTokens(tokens);
        }),
        catchError(err => {
          return throwError(() => err);
        })
      );
  }


  public getCurrentUser(): User | null {
    try {
      const tokens = this.getTokens();
      if (tokens && tokens.token) {
        const decodedToken = jwtDecode<{ user: User }>(tokens.token);
        return decodedToken.user || null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return null;
  }

  public getTokens(): Tokens|null {
    const encodedTokens = this.localStorage.get(this.tokenStorageKey);
    if (!encodedTokens) {
      return null;
    }
    return encodedTokens as Tokens;
  }

  public getRoles(): string[] {
    const user = this.getCurrentUser();
    if (!user) {
      return [];
    }
    return user.roles;
  }

  public saveTokens(tokens: Tokens): void {
    if (!tokens?.token) {
      return;
    }
    // Keep only the short-lived access token in browser storage.
    this.localStorage.save(this.tokenStorageKey, { token: tokens.token, refresh_token: null });
  }

  public logout(): void {
    this.http.post(`${environment.apiUrl}/api/v1/auth/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(null)))
      .subscribe();
    this.localStorage.remove(this.tokenStorageKey);
  }

  public isImpersonated(): boolean {
    const tokens = this.getTokens();
    if (!tokens?.token) {
      return false;
    }

    try {
      const payload = jwtDecode<any>(tokens.token);
      return payload.impersonated === true;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  public getImpersonatorId(): number | null {
    const tokens = this.getTokens();
    if (!tokens?.token) {
      return null;
    }

    try {
      const payload = jwtDecode<any>(tokens.token);
      return payload.impersonator_id ?? null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  public refreshUser(): void {
    this.refreshToken();
  }

  public requestPasswordReset(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${environment.apiUrl}/api/v1/user/request-password-reset`, {
      email: email
    }).pipe(
      catchError(() => of(false))
    );
  }

  public resetPassword(
    token: string,
    email: string,
    password: string,
    passwordConfirm: string,
  ): Observable<boolean> {
    const body: any = {
      token,
      email,
      password,
      passwordConfirm,
    };

    return this.http
      .post<Tokens>(`${environment.apiUrl}/api/v1/user/reset-password`, body, { withCredentials: true })
      .pipe(
        tap((tokens) => this.saveTokens(tokens)),
        map(() => true)
      );
  }
}
